import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  createSearchParams,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import { Grid, TextField } from '@mui/material';

import ConfirmationDialog from 'commons/components/ConfirmationDialog/ConfirmationDialog.component';
import { TableComponent } from 'commons/components/Table';
import { STOCheckBasket } from 'commons/types';
import {
  getOnProgressSTOCheckBasket,
  getSTOCheckBasket,
} from 'redux-stores/actions';
import { RootReducerInterface } from 'redux-stores/reducers';
import { setSTOCheckBasket } from 'redux-stores/reducers/stoCheckBasketReducer';
import { AppDispatch } from 'redux-stores/store';
import { STO_CHECK_ROUTES } from 'routes/constants';

import { BasketInfos } from './components';
import { CheckBasketStyle as S } from './STOCheckBasket.styles';
import { stoDataConfig } from './table-configs/stoData.config';

const STOCheckBasketPage: React.FC = () => {
  const { stoCheckBasket } = useSelector(
    (state: RootReducerInterface) => state.stoCheckBasket,
  );
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  const [basket, setBasket] = useState<number>(0);
  const [currentSTOCheckBasket, setCurrentSTOCheckBasket] =
    useState<STOCheckBasket | null>(null);
  const [openChangeSTOBasketModal, setOpenChangeSTOBasketModal] =
    useState<boolean>(false);
  const [disableContent, setDisableContent] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const onFieldChange = (value: string): void => {
    if (value === '') {
      setBasket(0);
      return;
    }
    const basketValue =
      typeof value === 'string' ? parseInt(value, 10) : (value as number);
    setBasket(basketValue);
  };

  const handleClickCheckDetails = (): void => {
    dispatch(getSTOCheckBasket(basket)).then(() => setBasket(0));
  };

  const getTotalQtyBySkuCode = (skuCode: string): number => {
    const qtyTotal = currentSTOCheckBasket?.items
      .flatMap((item) => {
        if (item.sku_code === skuCode) {
          return item.pick_actual.reduce(
            (sum, pickActual) => sum + pickActual.quantity,
            0,
          );
        }
        return 0;
      })
      .reduce((sum, data) => sum + data, 0);
    return qtyTotal ?? 0;
  };

  const handleSTOBasketChangeModalOnConfirm = () => {
    setCurrentSTOCheckBasket(stoCheckBasket);
    setOpenChangeSTOBasketModal(false);
  };

  // return state with currentSTOCheckBasket data
  const handleSTOBasketChangeModalOnClose = () => {
    setOpenChangeSTOBasketModal(false);
    dispatch(setSTOCheckBasket(currentSTOCheckBasket));
  };

  useEffect(() => {
    const stoNumber = searchParams.get('stoNumber') ?? '';
    const basketSearchParam = searchParams.get('basket');
    const stoBasket = basketSearchParam ? parseInt(basketSearchParam, 10) : 0;
    if (stoNumber !== '' && basket !== 0) {
      dispatch(getOnProgressSTOCheckBasket({ stoNumber, basket: stoBasket }));
    }
  }, [dispatch]);

  useEffect(() => {
    // check STO number & basket with currentSTOCheckBasket value
    // if different, set modal open for data changing
    if (stoCheckBasket) {
      if (
        currentSTOCheckBasket &&
        currentSTOCheckBasket.sto_number !== stoCheckBasket.sto_number
      ) {
        setOpenChangeSTOBasketModal(true);
      } else {
        setCurrentSTOCheckBasket(stoCheckBasket);
      }
    }
  }, [stoCheckBasket]);

  useEffect(() => {
    if (currentSTOCheckBasket) {
      setDisableContent(false);
      setSearchParams({
        stoNumber: currentSTOCheckBasket.sto_number,
        basket: currentSTOCheckBasket.basket.toString(),
      });
    } else {
      setDisableContent(true);
    }
  }, [currentSTOCheckBasket, setSearchParams]);

  const isAbleToProceed = (): boolean => {
    if (currentSTOCheckBasket) {
      const isAllChecked = currentSTOCheckBasket.items.every(
        (item) =>
          item.pick_actual.every((pickActual) => pickActual.check_passed) &&
          item.checking_metadata.total_qty_checked &&
          item.checking_metadata.total_qty_checked ===
            item.pick_actual.reduce((total, { quantity }) => {
              return total + quantity;
            }, 0) &&
          item.checking_metadata.total_qty_checked === item.quantity &&
          item.box_number &&
          item.pallet,
      );
      const isAllPhotoTaken = currentSTOCheckBasket.photo_url.length > 0;
      return isAllChecked && isAllPhotoTaken;
    } else {
      return false;
    }
  };

  return (
    <div>
      <Grid container style={{ padding: '16px 0' }}>
        <Grid xs={9}>
          <TextField
            placeholder="Basket Number"
            fullWidth
            variant="outlined"
            type="number"
            value={basket !== 0 ? basket : ''}
            onChange={(event) => onFieldChange(event.target.value)}
            inputProps={{
              'data-testid': 'InputCheckBasket',
            }}
          />
        </Grid>
        <Grid xs={3} style={{ textAlign: 'center' }}>
          <S.PrimaryButton
            onClick={() => handleClickCheckDetails()}
            disabled={basket === 0}
            data-testid="BtnCheckDetails"
          >
            CHECK DETAILS
          </S.PrimaryButton>
        </Grid>
      </Grid>
      {currentSTOCheckBasket ? (
        <div style={{ margin: '16px 0' }}>
          <TableComponent
            config={stoDataConfig}
            data={[currentSTOCheckBasket]}
          />
        </div>
      ) : null}
      <S.ContentWrapper>
        {currentSTOCheckBasket && (
          <BasketInfos
            key={currentSTOCheckBasket.basket}
            data={currentSTOCheckBasket}
            stoNumber={currentSTOCheckBasket.sto_number}
            getTotalQtyBySkuCode={getTotalQtyBySkuCode}
            disableContent={disableContent}
          />
        )}
        <S.BlankWhiteSpace />
      </S.ContentWrapper>
      <S.FooterWrapper>
        <Grid container>
          <Grid item xs={12}>
            <S.PrimaryButton
              disabled={!isAbleToProceed()}
              onClick={() =>
                navigate({
                  pathname: STO_CHECK_ROUTES.CHECK_SUMMARY,
                  search: createSearchParams({
                    stoNumber: stoCheckBasket?.sto_number || '',
                    basket: stoCheckBasket?.basket.toString() || '0',
                  }).toString(),
                })
              }
              data-testid="BtnSTOCheckProceed"
            >
              PROCEED
            </S.PrimaryButton>
          </Grid>
        </Grid>
      </S.FooterWrapper>

      {/* Modal for change PO over scan basket number */}
      <ConfirmationDialog
        title="Order Change"
        content={`Basket number scanned will change order from <b>${currentSTOCheckBasket?.sto_number}</b> to <b>${stoCheckBasket?.sto_number}</b>. Do you wish to change order checking?`}
        onClose={handleSTOBasketChangeModalOnClose}
        onConfirm={handleSTOBasketChangeModalOnConfirm}
        openModal={openChangeSTOBasketModal}
        callOnCloseOnHandleConfirm={false}
      />
    </div>
  );
};

export default STOCheckBasketPage;
